/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Constants and default values for table pagination, column widths,
 * and layout configuration shared across table components.
 */
import {VIEW_TYPE} from "../../../enums/ViewType";
import {SEARCH_OPERATION_MODEL} from "../../../pages/Modals/Model/Model";

export const DEFAULT_PAGINATION_START = 0;
export const DEFAULT_PAGINATION_LIST_SIZE = 20;
export const DEFAULT_PAGINATION_CARD_SIZE = 8;

export const DEFAULT_LEFT = 0;
export const CHECKBOX_TABLE_WIDTH = 50;
export const WIDTH_TOOLTIP_FIXED_COLUMN = 'w-180px'

export const LEFT_FIRST_COLUMN_WITH_CHECKBOX = 55;

export const WIDTH_COLUMN_FIXED = 200;

export const LEFT_SECOND_COLUMN_WITH_CHECKBOX = 251;
export const LEFT_SECOND_COLUMN_WITHOUT_CHECKBOX = 197;

export const SEARCH_USERS_MODEL = {
    pagination: { page: DEFAULT_PAGINATION_START, size: DEFAULT_PAGINATION_LIST_SIZE },
    model: null,
    modelId: null
};
export const SIMPLE_PAGINATION_MODEL ={
    order: true, // Sorting order
    page: DEFAULT_PAGINATION_START, // Current page number
    size: DEFAULT_PAGINATION_LIST_SIZE, // Number of rows per page
    sortBy: 'id', // Sorting by contract ID
    set: function ({ page, rowsPerPage }){
         return {
             order: true, // Sorting order
             page, // Current page number
             size: rowsPerPage, // Number of rows per page
             sortBy: 'id'
         }
    }
}

export const SEARCH_MODEL ={
    key:"",
    pagination: {
        order:false,
        page: DEFAULT_PAGINATION_START,
        size: DEFAULT_PAGINATION_LIST_SIZE,
        sortBy:'id'
    },
    model: null,
    searchCriteriaList:[],
    viewType : null,
    userId:null,
    setObject: function ({page, size, model, rootGroup= null, sortBy='id', order=true, key="",
                             view=null, viewType=null ,userId=null, userToFindId=null,id=null,
                             attendanceStartDate=null, attendanceEndDate=null, zoneId=null ,modelId =null,
                             attendanceTimeZone = null ,attendanceSearchType = null, userTypeIds=[]
    }) {
        let data = {
            pagination: {
                order,
                page,
                size,
                sortBy
            },
            model
        };
        if(key) Object.assign(data, { key });
        if(rootGroup) Object.assign(data, { rootGroup });
        if(view) Object.assign(data, {viewType : view === VIEW_TYPE.GRID.name ? VIEW_TYPE.GRID.viewType : VIEW_TYPE.LIST.viewType});
        if(viewType !== null) Object.assign(data, {viewType});
        if(userId) Object.assign(data, {userId});
        if(userToFindId) Object.assign(data, {userToFindId});
        if(id !== null) Object.assign(data, {id})
        if(zoneId !== null) Object.assign(data, {zoneId})
        if(attendanceStartDate !== null) Object.assign(data, {attendanceStartDate})
        if(attendanceEndDate !== null) Object.assign(data, {attendanceEndDate})
        if(attendanceTimeZone !== null) Object.assign(data, {attendanceTimeZone})
        if(attendanceSearchType !== null) Object.assign(data, {attendanceSearchType})
        if(modelId !== null) Object.assign(data, {modelId})
        if(userTypeIds && userTypeIds?.length > 0) Object.assign(data, {userTypeIds})
        return data;
    },
    setPagination: function ({page, size, rowsPerPage, sortBy='id', order=true}) {
        return {
            ...this,
            pagination: {
                order,
                page,
                size: size || rowsPerPage ,
                sortBy
            }
        }
    },
    clearResults: function (){
        return {
            content: [],
            empty: true,
            numberOfElements: 0,
            totalElements: 0,
            totalPages: 0
        }
    }
}

export const CRITERIA_MODEL = {
    /**
     * Converts the payload array into a rootGroup tree using left-fold (no operator precedence).
     *
     * Input shape (produced by SearchForApp.handleSearch — backward connector model):
     *   - Standalone rule: { dataOption, fieldType, filterKey, ... }
     *     dataOption = item.connector = how this item connects to the previous one.
     *   - Group: { rules: [{ dataOption, ...ruleFields }, ...] }
     *     rules[0].dataOption = group's outer connector; rules[i>0].dataOption = inner backward connector.
     *
     * Tree construction: left-fold over the list.
     *   acc = item[0]
     *   for each item[i] (i >= 1): acc = { dataOption: item[i].connector, left: acc, right: item[i] }
     * Groups fold their inner rules the same way, producing a sub-tree.
     *
     * Output: { dataOption, rules: [...leafRules], subGroups: [...childNodes] }
     *
     * @param {Array} list - Payload produced by SearchForApp.handleSearch.
     * @returns {Object|null} Nested rootGroup object, or null if list is empty.
     */
    set: function (list) {
        if (!list || list.length === 0) return null;

        const getConnector = (item) =>
            Array.isArray(item.rules) ? item.rules[0]?.dataOption : item.dataOption;

        const buildItemNode = (item) => {
            if (!Array.isArray(item.rules)) {
                const { dataOption, ...rule } = item;
                return { leaf: true, rule };
            }
            return buildTree(item.rules);
        };

        const buildTree = (items) => {
            if (items.length === 0) return null;
            let acc = buildItemNode(items[0]);
            for (let i = 1; i < items.length; i++) {
                const connector = getConnector(items[i]);
                acc = { leaf: false, dataOption: connector, left: acc, right: buildItemNode(items[i]) };
            }
            return acc;
        };

        const toAPI = (node) => {
            const result = { dataOption: node.dataOption, rules: [], subGroups: [] };
            const add = (child) => {
                if (child.leaf) result.rules.push(child.rule);
                else result.subGroups.push(toAPI(child));
            };
            add(node.left);
            add(node.right);
            return result;
        };

        const tree = buildTree(list);
        if (!tree) return null;
        if (tree.leaf) return { dataOption: SEARCH_OPERATION_MODEL.AND, rules: [tree.rule], subGroups: [] };
        return toAPI(tree);
    }
}

/**
 * Calculates and applies dynamic widths to the table header and body cells
 * based on the content size, available table width, and optional checkbox column.
 *
 * @param {Object} params - Configuration object.
 * @param {string} params.tableId - DOM id of the target table.
 * @param {Array} params.rows - Data rows used to determine whether width calculation should run.
 * @param {boolean} params.checkbox - Indicates whether the table includes a leading checkbox column.
 * @param {Array} params.thead - Header definition array used to validate that columns exist.
 * @returns {void}
 */
export const applyTableWidths = ({ tableId, rows, checkbox, thead }) => {
    const table = document.getElementById(tableId);
    if (!table || !thead?.length || !rows.length) return;
    if (table.offsetWidth === 0) return;

    const ths = Array.from(document.querySelectorAll(`#${tableId} th`));
    const tdsRows = Array.from(document.querySelectorAll(`#${tableId} tbody tr`));

    if (!ths.length) return;

    const totalColumns = ths.length;

    ths.forEach((th, index) => {
        th.classList.remove('wide-first-th', 'align-left');
        th.style.width = '';

        if (totalColumns < 8 && index === 0) {
            th.classList.add('wide-first-th');
        }
    });

    tdsRows.forEach((row) => {
        [...row.children].forEach((td, index) => {
            td.classList.remove('wide-first-td', 'align-left');
            td.style.width = '';

            if (totalColumns < 8 && index === 0) {
                td.classList.add('wide-first-td');
            }
        });
    });

    let width = 0;

    ths.forEach((th, i) => {
        const measuredWidth = getTableMaxWidth({ tableId, rows, th, index: i });
        width += measuredWidth;

        tdsRows.forEach((row) => {
            const tds = row.getElementsByTagName('td');
            Array.from(tds).forEach((td, tdIndex) => {
                if (tdIndex === i && td) {
                    td.style.width = `${measuredWidth}px`;
                }
            });
        });
    });

    assignTableWidth({ table, width, ths, checkbox });

    ths.forEach((th, i) => {
        const finalWidth = th.style.width;

        tdsRows.forEach((row) => {
            const tds = row.getElementsByTagName('td');
            Array.from(tds).forEach((td, tdIndex) => {
                if (tdIndex === i && td) {
                    td.style.width = finalWidth;
                }
            });
        });
    });
}

/**
 * Measures the maximum width required for a specific table column.
 *
 * @param {Object} params - Configuration object.
 * @param {string} params.tableId - DOM id of the target table.
 * @param {Array} params.rows - Data rows used to validate whether measurement should run.
 * @param {HTMLElement} params.th - Header cell element for the current column.
 * @param {number} params.index - Zero-based column index.
 * @returns {number} The calculated maximum width for the column in pixels.
 */
const getTableMaxWidth = ({ tableId, rows, th, index }) => {
    if (!rows.length) return 0;

    const customWidthClass = [...th.classList]
        .find(c => c.includes('custom-width-'));

    const customWidth = customWidthClass?.replace('custom-width-', '');

    if (customWidth) {
        th.style.width = `${customWidth}px`;
        return parseInt(customWidth, 10);
    }

    const cells = document.querySelectorAll(`#${tableId} tr td:nth-child(${index + 1})`);
    let maxWidth = Math.max(th.scrollWidth, th.offsetWidth);

    cells.forEach((cell) => {
        maxWidth = Math.max(maxWidth, cell.scrollWidth, cell.offsetWidth);
    });

    th.style.width = `${maxWidth}px`;
    return maxWidth;
}

/**
 * Distributes the remaining available table width across all visible columns.
 *
 * When the table contains fewer than eight columns, the first content column
 * receives a larger proportion of the remaining width. If a checkbox column is enabled,
 * it is excluded from the width redistribution logic.
 *
 * @param {Object} params - Configuration object.
 * @param {HTMLTableElement} params.table - Table element whose total width is used as the available space reference.
 * @param {number} params.width - Sum of the measured widths already assigned to all columns.
 * @param {HTMLElement[]} params.ths - Array of header cell elements.
 * @param {boolean} params.checkbox - Indicates whether the table includes a leading checkbox column.
 * @returns {void}
 */
const assignTableWidth = ({ table, width, ths, checkbox }) => {
    let checkboxWidth = 0;

    if (checkbox) {
        checkboxWidth = ths[0]?.offsetWidth || 0;
    }

    const totalWidth = table.offsetWidth - width + checkboxWidth;
    const start = checkbox ? 1 : 0;
    const totalCols = ths.length - start;

    if (totalCols <= 0 || totalWidth <= 0) return;

    if (totalCols < 8) {
        const restCols = totalCols - 1;
        const unit = totalWidth / (restCols + 6);

        for (let i = 0; i < ths.length; i++) {
            if (checkbox && i === 0) continue;

            if (i === start) {
                setTableWidth(ths[i], unit * 6);
            } else {
                setTableWidth(ths[i], unit);
            }
        }
    } else {
        const unit = totalWidth / totalCols;

        for (let i = 0; i < ths.length; i++) {
            if (checkbox && i === 0) continue;
            setTableWidth(ths[i], unit);
        }
    }
};

/**
 * Adds an extra width value to the current inline width of a header cell.
 *
 * @param {HTMLElement} th - Header cell element whose width will be updated.
 * @param {number} extraWidth - Extra width in pixels to add to the current width.
 * @returns {void}
 */
const setTableWidth = (th, extraWidth) => {
    const currentWidth = parseInt((th.style.width || '0').replace('px', ''), 10) || 0;
    th.style.width = `${currentWidth + extraWidth}px`;
};

/**
 * Sets CSS custom properties used to calculate the visible height and sticky positioning
 * for the table header and body container.
 *
 * These values depend on whether the table is rendered inside a modal or a regular layout.
 *
 * @param {Object} params - Configuration object.
 * @param {boolean} params.withoutHeight - Determines whether dynamic height calculation is disabled.
 * @param {React.RefObject<HTMLElement>} params.elementRef - Reference to the main table container element.
 * @param {React.RefObject<HTMLElement>} params.tbodyRef - Reference to the table body container element.
 * @param {boolean} params.inModal - Indicates whether the table is displayed inside a modal.
 * @returns {void}
 */
export const setTheadTBodyHeight = ({withoutHeight, elementRef, tbodyRef, inModal}) =>{
    if (withoutHeight === false) {
        if (elementRef.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const distanceTop = inModal ? 400 : rect.top + 2;
            elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
        }

        if (tbodyRef.current) {
            const rect = tbodyRef.current.getBoundingClientRect();
            const distanceTop = inModal ? 300 : rect.top;
            tbodyRef.current.style.setProperty('--tbody-top-value', `${distanceTop}px`);
        }
    }
}

